import * as Sentry from "@sentry/nextjs";

import { trackAmplitudeEvent } from "@/lib/analytics/amplitude";
import { ANALYTICS_EVENTS } from "@/lib/analytics/catalog";
import type { EventName } from "@/lib/analytics/types";
import { getAppVersion, getClientBuildId } from "@/lib/app-version";

export const STALE_SERVER_ACTION_MESSAGE_PATTERN =
  /Failed to find Server Action(?: "([^"]+)")?/i;

const RELOAD_GUARD_TTL_MS = 30 * 60 * 1000;
const RELOAD_DELAY_MS = 400;
const VERSION_POLL_INTERVAL_MS = 5 * 60 * 1000;
const TAB_SESSION_KEY = "aseed-stale-server-action-tab-session";

export type StaleServerActionInfo = {
  actionId: string | null;
  message: string;
};

type ReloadGuardRecord = {
  timestamp: number;
  pathname: string;
  actionId: string | null;
};

type VersionPayload = {
  appVersion?: string;
  buildId?: string;
  release?: string;
  generatedAt?: string;
};

type MismatchKind = "same-build-or-unknown" | "version-drift";
type ReloadStrategy = "none" | "immediate" | "on-visible" | "manual";
type TelemetrySignal =
  | "mismatch-detected"
  | "version-drift-detected"
  | "recovery-blocked"
  | "proactive-reload-triggered";

type PendingDriftRecord = {
  timestamp: number;
  serverBuildId: string;
  serverAppVersion?: string;
  serverRelease?: string;
};

type TelemetryContext = {
  actionId: string | null;
  message: string;
  pathname: string;
  search: string;
  currentAppVersion: string;
  currentBuildId: string;
  documentReferrer: string;
  navigationType: string;
  tabSessionId: string;
  reloadedOnce: boolean;
  mismatchKind: MismatchKind;
  reloadStrategy: ReloadStrategy;
  staleTabDetected: boolean;
  versionDriftDetected: boolean;
  signal: TelemetrySignal;
  serverAppVersion?: string;
  serverBuildId?: string;
  serverRelease?: string;
};

type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;
type LocationLike = Pick<Location, "pathname" | "search" | "reload">;
type DocumentLike = Pick<
  Document,
  "referrer" | "visibilityState" | "addEventListener" | "removeEventListener"
>;
type WindowLike = {
  addEventListener: Window["addEventListener"];
  removeEventListener: Window["removeEventListener"];
  __NEXT_DATA__?: {
    buildId?: string;
  };
};
type SetTimeoutLike = (
  handler: () => void,
  timeout: number
) => ReturnType<typeof setTimeout>;
type ClearTimeoutLike = (handle: ReturnType<typeof setTimeout>) => void;
type SetIntervalLike = (
  handler: () => void,
  timeout: number
) => ReturnType<typeof setInterval>;
type ClearIntervalLike = (handle: ReturnType<typeof setInterval>) => void;

export type ToastKind = "refreshing" | "manual-refresh" | "version-drift";

export type StaleServerActionRuntimeOptions = {
  appVersion?: string;
  buildId?: string;
  sessionStorage?: StorageLike;
  location?: LocationLike;
  document?: DocumentLike;
  targetWindow?: WindowLike;
  performanceApi?: Pick<Performance, "getEntriesByType">;
  fetchImpl?: typeof fetch;
  now?: () => number;
  setTimeoutImpl?: SetTimeoutLike;
  clearTimeoutImpl?: ClearTimeoutLike;
  setIntervalImpl?: SetIntervalLike;
  clearIntervalImpl?: ClearIntervalLike;
  onToast?: (kind: ToastKind, context: TelemetryContext) => void;
  trackEvent?: (eventName: string, payload: Record<string, unknown>) => void;
  captureSentry?: (context: TelemetryContext) => void;
  reloadDelayMs?: number;
  reloadGuardTtlMs?: number;
  versionPollIntervalMs?: number;
  versionEndpoint?: string;
};

function createSessionId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `stale-action-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function isRecordFresh(
  record: ReloadGuardRecord | null,
  now: number,
  ttlMs: number
): boolean {
  if (!record) {
    return false;
  }

  return now - record.timestamp < ttlMs;
}

function readStoredJson<T>(
  storage: StorageLike | undefined,
  key: string
): T | null {
  if (!storage) {
    return null;
  }

  try {
    const raw = storage.getItem(key);
    if (!raw) {
      return null;
    }

    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function writeStoredJson(
  storage: StorageLike | undefined,
  key: string,
  value: unknown
): void {
  if (!storage) {
    return;
  }

  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore storage write failures in privacy-restricted environments.
  }
}

function readStoredString(storage: StorageLike | undefined, key: string): string | null {
  if (!storage) {
    return null;
  }

  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function writeStoredString(
  storage: StorageLike | undefined,
  key: string,
  value: string
): void {
  if (!storage) {
    return;
  }

  try {
    storage.setItem(key, value);
  } catch {
    // Ignore storage write failures in privacy-restricted environments.
  }
}

function removeStoredValue(storage: StorageLike | undefined, key: string): void {
  if (!storage) {
    return;
  }

  try {
    storage.removeItem(key);
  } catch {
    // Ignore storage failures in privacy-restricted environments.
  }
}

function buildReloadGuardKey(buildId: string): string {
  return `aseed-stale-server-action-reload:${buildId}`;
}

function buildDriftGuardKey(serverBuildId: string): string {
  return `aseed-stale-server-action-version-drift:${serverBuildId}`;
}

function buildPendingDriftKey(serverBuildId: string): string {
  return `aseed-stale-server-action-pending:${serverBuildId}`;
}

function getTabSessionId(storage: StorageLike | undefined): string {
  const stored = readStoredString(storage, TAB_SESSION_KEY);
  if (stored) {
    return stored;
  }

  const sessionId = createSessionId();
  writeStoredString(storage, TAB_SESSION_KEY, sessionId);
  return sessionId;
}

function getNavigationType(
  performanceApi?: Pick<Performance, "getEntriesByType">
): string {
  try {
    const entry = performanceApi
      ?.getEntriesByType("navigation")
      ?.find((candidate): candidate is PerformanceNavigationTiming => {
        return typeof candidate === "object" && candidate !== null && "type" in candidate;
      });

    return entry?.type ?? "unknown";
  } catch {
    return "unknown";
  }
}

function getErrorMessage(input: unknown): string | null {
  if (typeof input === "string") {
    return input;
  }

  if (input instanceof Error) {
    return input.message;
  }

  if (
    typeof input === "object" &&
    input !== null &&
    "message" in input &&
    typeof (input as { message?: unknown }).message === "string"
  ) {
    return (input as { message: string }).message;
  }

  if (
    typeof input === "object" &&
    input !== null &&
    "reason" in input
  ) {
    return getErrorMessage((input as { reason?: unknown }).reason);
  }

  return null;
}

export function extractStaleServerActionInfo(
  input: unknown
): StaleServerActionInfo | null {
  const message = getErrorMessage(input);
  if (!message) {
    return null;
  }

  const matched = STALE_SERVER_ACTION_MESSAGE_PATTERN.exec(message);
  if (!matched) {
    return null;
  }

  return {
    actionId: matched[1] ?? null,
    message,
  };
}

function defaultTrackEvent(eventName: string, payload: Record<string, unknown>): void {
  trackAmplitudeEvent(eventName as EventName, payload);
}

function defaultCaptureSentry(context: TelemetryContext): void {
  const message =
    context.signal === "version-drift-detected"
      ? "stale_server_action_version_drift_detected"
      : context.signal === "proactive-reload-triggered"
        ? "stale_server_action_proactive_reload_triggered"
        : context.signal === "recovery-blocked"
          ? "stale_server_action_recovery_blocked"
          : "stale_server_action_detected";

  Sentry.captureMessage(message, {
    level: "warning",
    tags: {
      component: "aseed-client",
      incident: "stale-server-action",
      stale_server_action: "true",
      app_version: context.currentAppVersion,
      build_id: context.currentBuildId,
      path: context.pathname,
      action_id: context.actionId ?? "unknown",
      tab_session_id: context.tabSessionId,
      reloaded_once: String(context.reloadedOnce),
      mismatch_kind: context.mismatchKind,
      reload_strategy: context.reloadStrategy,
      stale_tab_detected: String(context.staleTabDetected),
      telemetry_signal: context.signal,
      version_drift_detected: String(context.versionDriftDetected),
    },
    extra: {
      search: context.search,
      message: context.message,
      document_referrer: context.documentReferrer,
      navigation_type: context.navigationType,
      server_app_version: context.serverAppVersion,
      server_build_id: context.serverBuildId,
      server_release: context.serverRelease,
    },
  });
}

export function createStaleServerActionRuntime(
  options: StaleServerActionRuntimeOptions = {}
) {
  const sessionStorageRef =
    options.sessionStorage ??
    (typeof window !== "undefined" ? window.sessionStorage : undefined);
  const locationRef =
    options.location ?? (typeof window !== "undefined" ? window.location : undefined);
  const documentRef =
    options.document ?? (typeof document !== "undefined" ? document : undefined);
  const targetWindow =
    options.targetWindow ?? (typeof window !== "undefined" ? window : undefined);
  const performanceApi =
    options.performanceApi ??
    (typeof performance !== "undefined" ? performance : undefined);
  const fetchImpl =
    options.fetchImpl ?? (typeof fetch === "function" ? fetch.bind(globalThis) : undefined);
  const now = options.now ?? (() => Date.now());
  const setTimeoutImpl = options.setTimeoutImpl ?? setTimeout;
  const clearTimeoutImpl = options.clearTimeoutImpl ?? clearTimeout;
  const setIntervalImpl = options.setIntervalImpl ?? setInterval;
  const clearIntervalImpl = options.clearIntervalImpl ?? clearInterval;
  const currentAppVersion = options.appVersion ?? getAppVersion();
  const currentBuildId = options.buildId ?? getClientBuildId(targetWindow as Window & {
    __NEXT_DATA__?: {
      buildId?: string;
    };
  });
  const versionEndpoint = options.versionEndpoint ?? "/api/version";
  const onToast = options.onToast;
  const trackEvent = options.trackEvent ?? defaultTrackEvent;
  const captureSentry = options.captureSentry ?? defaultCaptureSentry;
  const reloadDelayMs = options.reloadDelayMs ?? RELOAD_DELAY_MS;
  const reloadGuardTtlMs = options.reloadGuardTtlMs ?? RELOAD_GUARD_TTL_MS;
  const versionPollIntervalMs =
    options.versionPollIntervalMs ?? VERSION_POLL_INTERVAL_MS;

  let intervalId: ReturnType<typeof setInterval> | null = null;
  let pendingReloadTimer: ReturnType<typeof setTimeout> | null = null;
  let pendingVisibleDrift: PendingDriftRecord | null = null;
  let knownVersionDrift: PendingDriftRecord | null = null;

  const baseContext = (
    info: StaleServerActionInfo,
    extras: Partial<TelemetryContext> = {}
  ): TelemetryContext => {
    return {
      actionId: info.actionId,
      message: info.message,
      pathname: locationRef?.pathname ?? "/",
      search: locationRef?.search ?? "",
      currentAppVersion,
      currentBuildId,
      documentReferrer: documentRef?.referrer ?? "",
      navigationType: getNavigationType(performanceApi),
      tabSessionId: getTabSessionId(sessionStorageRef),
      reloadedOnce: false,
      mismatchKind: "same-build-or-unknown",
      reloadStrategy: "none",
      staleTabDetected: false,
      versionDriftDetected: false,
      signal: "mismatch-detected",
      ...extras,
    };
  };

  const getReloadGuardState = () => {
    const reloadKey = buildReloadGuardKey(currentBuildId);
    const storedGuard = readStoredJson<ReloadGuardRecord>(sessionStorageRef, reloadKey);
    return {
      reloadKey,
      reloadedOnce: isRecordFresh(storedGuard, now(), reloadGuardTtlMs),
    };
  };

  const writeReloadGuard = (context: TelemetryContext) => {
    writeStoredJson(sessionStorageRef, buildReloadGuardKey(currentBuildId), {
      timestamp: now(),
      pathname: context.pathname,
      actionId: context.actionId,
    } satisfies ReloadGuardRecord);
  };

  const scheduleReload = () => {
    if (pendingReloadTimer !== null) {
      clearTimeoutImpl(pendingReloadTimer);
    }

    pendingReloadTimer = setTimeoutImpl(() => {
      locationRef?.reload();
    }, reloadDelayMs);
  };

  const clearPendingVisibleDrift = () => {
    if (!pendingVisibleDrift) {
      return;
    }

    removeStoredValue(
      sessionStorageRef,
      buildPendingDriftKey(pendingVisibleDrift.serverBuildId)
    );
    pendingVisibleDrift = null;
  };

  const createDriftContext = (
    drift: PendingDriftRecord,
    signal: TelemetrySignal,
    reloadStrategy: ReloadStrategy,
    extras: Partial<TelemetryContext> = {}
  ) => {
    return baseContext(
      {
        actionId: null,
        message: "version_drift_detected",
      },
      {
        mismatchKind: "version-drift",
        reloadStrategy,
        staleTabDetected: true,
        versionDriftDetected: true,
        signal,
        serverAppVersion: drift.serverAppVersion,
        serverBuildId: drift.serverBuildId,
        serverRelease: drift.serverRelease,
        ...extras,
      }
    );
  };

  const armVisibleDriftReload = (drift: PendingDriftRecord) => {
    pendingVisibleDrift = drift;
    writeStoredJson(sessionStorageRef, buildPendingDriftKey(drift.serverBuildId), drift);
  };

  const reportRecoveryBlocked = (context: TelemetryContext) => {
    const blockedContext = {
      ...context,
      reloadedOnce: true,
      reloadStrategy: "manual" as const,
      signal: "recovery-blocked" as const,
    };
    captureSentry(blockedContext);
    trackEvent(ANALYTICS_EVENTS.SERVER_ACTION_RECOVERY_BLOCKED, blockedContext);
    onToast?.("manual-refresh", blockedContext);
  };

  const triggerPendingVisibleReload = () => {
    if (!pendingVisibleDrift || documentRef?.visibilityState !== "visible") {
      return false;
    }

    const drift = pendingVisibleDrift;
    clearPendingVisibleDrift();

    const { reloadedOnce } = getReloadGuardState();
    const context = createDriftContext(drift, "proactive-reload-triggered", "immediate", {
      reloadedOnce,
    });

    if (reloadedOnce) {
      reportRecoveryBlocked(context);
      return true;
    }

    writeReloadGuard(context);
    onToast?.("refreshing", context);
    scheduleReload();
    return true;
  };

  const handleMismatch = (input: unknown): boolean => {
    const info = extractStaleServerActionInfo(input);
    if (!info) {
      return false;
    }

    const { reloadedOnce } = getReloadGuardState();
    const context = baseContext(info, {
      reloadedOnce,
      mismatchKind: knownVersionDrift ? "version-drift" : "same-build-or-unknown",
      reloadStrategy: reloadedOnce ? "manual" : "immediate",
      staleTabDetected: knownVersionDrift !== null,
      versionDriftDetected: knownVersionDrift !== null,
      serverAppVersion: knownVersionDrift?.serverAppVersion,
      serverBuildId: knownVersionDrift?.serverBuildId,
      serverRelease: knownVersionDrift?.serverRelease,
    });
    captureSentry(context);
    trackEvent(ANALYTICS_EVENTS.SERVER_ACTION_MISMATCH_DETECTED, context);

    if (reloadedOnce) {
      reportRecoveryBlocked(context);
      return true;
    }

    writeReloadGuard(context);
    onToast?.("refreshing", context);
    scheduleReload();
    return true;
  };

  const checkVersionDrift = async (): Promise<void> => {
    if (!fetchImpl) {
      return;
    }

    let payload: VersionPayload;

    try {
      const response = await fetchImpl(versionEndpoint, {
        method: "GET",
        cache: "no-store",
        headers: {
          "Cache-Control": "no-store",
        },
      });

      if (!response.ok) {
        return;
      }

      payload = (await response.json()) as VersionPayload;
    } catch {
      return;
    }

    const serverBuildId =
      typeof payload.buildId === "string" && payload.buildId.length > 0
        ? payload.buildId
        : undefined;
    const serverAppVersion =
      typeof payload.appVersion === "string" && payload.appVersion.length > 0
        ? payload.appVersion
        : undefined;

    const driftDetected =
      (serverBuildId !== undefined && serverBuildId !== currentBuildId) ||
      (serverAppVersion !== undefined && serverAppVersion !== currentAppVersion);

    if (!driftDetected || !serverBuildId) {
      knownVersionDrift = null;
      clearPendingVisibleDrift();
      return;
    }

    const drift: PendingDriftRecord = {
      timestamp: now(),
      serverBuildId,
      serverAppVersion,
      serverRelease:
        typeof payload.release === "string" && payload.release.length > 0
          ? payload.release
          : undefined,
    };
    knownVersionDrift = drift;

    const reloadStrategy =
      documentRef?.visibilityState === "hidden" ? "immediate" : "on-visible";
    const driftContext = createDriftContext(
      drift,
      "version-drift-detected",
      reloadStrategy
    );

    const driftGuardKey = buildDriftGuardKey(serverBuildId);
    const alreadyNotified = readStoredString(sessionStorageRef, driftGuardKey);
    if (!alreadyNotified) {
      writeStoredString(sessionStorageRef, driftGuardKey, String(now()));
      captureSentry(driftContext);
      trackEvent(ANALYTICS_EVENTS.SERVER_ACTION_VERSION_DRIFT_DETECTED, driftContext);
    }

    const { reloadedOnce } = getReloadGuardState();
    const proactiveContext = createDriftContext(
      drift,
      "proactive-reload-triggered",
      reloadStrategy,
      { reloadedOnce }
    );

    if (!alreadyNotified) {
      captureSentry(proactiveContext);
      trackEvent(
        ANALYTICS_EVENTS.SERVER_ACTION_PROACTIVE_RELOAD_TRIGGERED,
        proactiveContext
      );
    }

    if (documentRef?.visibilityState === "hidden") {
      clearPendingVisibleDrift();

      if (reloadedOnce) {
        return;
      }

      writeReloadGuard(proactiveContext);
      scheduleReload();
      return;
    }

    armVisibleDriftReload(drift);
    if (!alreadyNotified) {
      onToast?.("version-drift", proactiveContext);
    }
  };

  const errorListener = (event: ErrorEvent) => {
    handleMismatch(event.error ?? event.message);
  };

  const rejectionListener = (event: PromiseRejectionEvent) => {
    handleMismatch(event.reason);
  };

  const visibilityListener = () => {
    if (documentRef?.visibilityState === "visible") {
      if (triggerPendingVisibleReload()) {
        return;
      }

      void checkVersionDrift();
    }
  };

  return {
    handleMismatch,
    checkVersionDrift,
    start() {
      targetWindow?.addEventListener("error", errorListener);
      targetWindow?.addEventListener("unhandledrejection", rejectionListener);
      documentRef?.addEventListener("visibilitychange", visibilityListener);

      void checkVersionDrift();

      intervalId = setIntervalImpl(() => {
        void checkVersionDrift();
      }, versionPollIntervalMs);
    },
    stop() {
      targetWindow?.removeEventListener("error", errorListener);
      targetWindow?.removeEventListener("unhandledrejection", rejectionListener);
      documentRef?.removeEventListener("visibilitychange", visibilityListener);

      if (intervalId !== null) {
        clearIntervalImpl(intervalId);
        intervalId = null;
      }

      if (pendingReloadTimer !== null) {
        clearTimeoutImpl(pendingReloadTimer);
        pendingReloadTimer = null;
      }
    },
  };
}
